/**
 * Auth host login redirect for handshake (adapted from prequal-ui).
 *
 * Unlike prequal we do **not** collect the phone number in PPUI — Auth owns
 * phone + OTP. After OTP, Auth sets `tamara-checkout-token` on `.tamara.co`
 * and sends the browser to `redirect_url`.
 *
 * Shape:
 *   {VITE_AUTH_LOGIN_URL}?client_type=CHECKOUT&redirect_url={checkoutOrigin}/?checkoutId=…
 *
 * Auth extracts `checkoutId` from the redirect_url query (not only from
 * `/checkout/{id}` path forms).
 */

export const AUTH_CLIENT_TYPE = "CHECKOUT";

/**
 * Marker appended to the Auth `redirect_url` so PPUI can tell, on the landing
 * page after OTP, that the shopper is returning from the `/aui` login flow
 * (funnel step `CHECKOUT_RETURNED_FROM_AUTH`). Auth preserves extra query
 * params on `redirect_url`, so this survives the round-trip.
 *
 * Non-sensitive by design — never carry tokens or PII on the URL.
 */
export const AUTH_RETURN_MARKER_PARAM = "from";
export const AUTH_RETURN_MARKER_VALUE = "aui";

/**
 * Marker on the Auth `redirect_url` when PPUI sent the shopper back to login
 * because their session fetch returned 401 (CXO-3293). The landing page reads
 * it to tell a successful re-login from one that still 401s, and to stop a
 * second redirect (loop guard). Non-sensitive by design.
 */
export const REAUTH_MARKER_PARAM = "reauth";
export const REAUTH_MARKER_VALUE = "1";

export interface AuthLoginRedirectParams {
  checkoutId: string;
  /** Optional extras to forward on redirect_url (e.g. locale). */
  search?: string;
  authLoginUrl?: string;
  checkoutRedirectBaseUrl?: string;
  /**
   * When true, tag the returned URL with `from=aui` so the landing page can
   * emit the return-from-auth funnel event. Only the Auth login path sets this;
   * the in-store exit (which never visits Auth) must not.
   */
  markFromAuth?: boolean;
  /** When true, tag the returned URL with `reauth=1` (see REAUTH_MARKER_PARAM). */
  markReauth?: boolean;
}

/**
 * Query params carried from the handshake entry URL onto the checkout URL.
 *
 * `mobile_loaded_handshake` must survive so the checkout screen can detect the
 * native app's ready-signal opt-in from the URL directly, rather than relying
 * on sessionStorage surviving a cross-origin Auth round-trip inside the webview.
 * `instore_checkout` keeps the in-store context on the URL. Both names are a
 * contract shared with the native app (see `@/lib/instore-handshake`).
 */
const FORWARDED_QUERY_PARAMS = [
  "locale",
  "mobile_loaded_handshake",
  "instore_checkout",
  // Carried through so the post-auth landing can attach order_id to the
  // CHECKOUT_RETURNED_FROM_AUTH funnel event (checkout_id remains the join key).
  "orderId",
] as const;

function requireEnv(name: string, value: string | undefined): string {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    throw new Error(`[handshake] ${name} is not configured`);
  }
  return trimmed;
}

/**
 * Builds the post-auth return URL Auth will bounce to after login.
 * e.g. https://checkout-staging.tamara.co/?checkoutId=abc
 *     https://checkout.tamara.co/v2/?checkoutId=abc  (prod, temporary /v2 base)
 */
export function buildCheckoutAuthRedirectUrl({
  checkoutId,
  search = "",
  checkoutRedirectBaseUrl = import.meta.env.VITE_AUTH_REDIRECT_BASE_URL,
  markFromAuth = false,
  markReauth = false,
}: Omit<AuthLoginRedirectParams, "authLoginUrl">): string {
  const base = requireEnv(
    "VITE_AUTH_REDIRECT_BASE_URL",
    checkoutRedirectBaseUrl,
  );
  const redirectUrl = new URL(base.endsWith("/") ? base : `${base}/`);
  const fromSearch = new URLSearchParams(search);
  // Canonical id wins over any stale query value.
  redirectUrl.searchParams.set("checkoutId", checkoutId);
  for (const key of FORWARDED_QUERY_PARAMS) {
    const value = fromSearch.get(key);
    if (value) redirectUrl.searchParams.set(key, value);
  }
  if (markFromAuth) {
    redirectUrl.searchParams.set(
      AUTH_RETURN_MARKER_PARAM,
      AUTH_RETURN_MARKER_VALUE,
    );
  }
  if (markReauth) {
    redirectUrl.searchParams.set(REAUTH_MARKER_PARAM, REAUTH_MARKER_VALUE);
  }
  return redirectUrl.toString();
}

/**
 * Builds the direct checkout URL for the in-store handshake exit.
 *
 * In-store skips Auth entirely — the native app already authenticated the
 * customer and set the checkout token cookie — so we navigate straight to the
 * checkout screen (the same destination Auth would otherwise bounce back to),
 * carrying the handshake params so the checkout screen can emit the native
 * "ready" signal the app is waiting on.
 */
export function buildInstoreCheckoutUrl(
  params: Omit<AuthLoginRedirectParams, "authLoginUrl">,
): string {
  return buildCheckoutAuthRedirectUrl(params);
}

/**
 * Builds the Tamara Auth login URL for an unauthenticated handshake exit.
 */
export function buildAuthLoginUrl({
  checkoutId,
  search = "",
  authLoginUrl = import.meta.env.VITE_AUTH_LOGIN_URL,
  checkoutRedirectBaseUrl = import.meta.env.VITE_AUTH_REDIRECT_BASE_URL,
  markReauth = false,
}: AuthLoginRedirectParams): string {
  const loginBase = requireEnv("VITE_AUTH_LOGIN_URL", authLoginUrl);
  const loginUrl = new URL(loginBase);
  loginUrl.searchParams.set("client_type", AUTH_CLIENT_TYPE);
  loginUrl.searchParams.set(
    "redirect_url",
    buildCheckoutAuthRedirectUrl({
      checkoutId,
      search,
      checkoutRedirectBaseUrl,
      markFromAuth: true,
      markReauth,
    }),
  );
  return loginUrl.toString();
}
